import { CommanderAPI } from '../../api/CommanderAPI';
import Util from '../../../../util/Util';
import { restClient as restClientHttp } from '../../../../axios';
import convertPromiseToQueryReturnValue from '../../../../axios/convertPromiseToQueryReturnValue';
import {
  DeviceRemoteDesktopAdapter,
  //DeviceRemoteDesktopSupportResponse,
  RemoteDesktopStatus,
} from './adapter/DeviceRemoteDesktopAdapter';
import { DeviceRemoteDesktopAPIService } from './DeviceRemoteDesktopAPIService';
import _ from 'lodash';

/**
 * API for checking device-specific configuration and capabilities.
 * Injects endpoints into the main CommanderAPI following the commander-status pattern.
 *
 * Capabilities include:
 * - Remote Desktop Support (bulk and single device queries)
 * - Other device configuration checks
 */

const deviceConfigURL = new Util().getDeviceConfigServiceURL();

export const DeviceConfigurationAPI = CommanderAPI.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Bulk query: Check remote desktop support for multiple devices
     *
     * Endpoint: POST /remotecontrol/available
     * Request: { deviceIds: string[] }
     * Response: { [deviceId]: RemoteDesktopStatus }
     *
     * Returns normalized entity format for caching and polling
     */
    getDevicesRemoteDesktopSupport: build.query<
      ReturnType<typeof DeviceRemoteDesktopAdapter.getInitialState>,
      string[] // array of deviceIDs
    >({
      queryFn: (deviceIds: string[]) => {
        const response = new DeviceRemoteDesktopAPIService()
          .getDevicesRemoteDesktopSupport(_.uniq(deviceIds))
          .then((data) =>
            DeviceRemoteDesktopAdapter.setAll(
              DeviceRemoteDesktopAdapter.getInitialState(),
              data
            )
          );
        return convertPromiseToQueryReturnValue(response);
      },
      providesTags: (result, error, deviceIds) =>
        deviceIds.map((id) => ({
          type: 'device-configuration-remote-desktop',
          id,
        })),
    }),
    /**
     * Single device query: Check if device supports remote desktop access
     *
     * Endpoint: GET /remotecontrol/{deviceID}/available
     *
     * Returns: { status: RemoteDesktopStatus; reason?: string }
     *
     * This query is kept for backward compatibility and detail view scenarios.
     * For list views, prefer getDevicesRemoteDesktopSupport (bulk) for efficiency.
     */
    getDeviceRemoteDesktopSupport: build.query<
      { status: RemoteDesktopStatus; reason?: string },
      string // deviceID
    >({
      queryFn: async (deviceId: string) => {
        try {
          const url = `${deviceConfigURL}/remotecontrol/${encodeURIComponent(
            deviceId
          )}/available`;

          // RestClient unwraps response.data, so response is the data object directly
          const response = await restClientHttp.get<{
            [key: string]: RemoteDesktopStatus;
            reason?: string;
          }>(url);

          const remoteDesktopStatus = response[deviceId];
          return {
            data: { status: remoteDesktopStatus, reason: response.reason },
          };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status || 500,
              data: error.response?.data || error.message,
            },
          };
        }
      },
      providesTags: (result, error, deviceId) => [
        { type: 'device-configuration', id: deviceId },
      ],
    }),
  }),
});

export const {
  useGetDeviceRemoteDesktopSupportQuery,
  useGetDevicesRemoteDesktopSupportQuery,
} = DeviceConfigurationAPI;
